import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import api from '../../utils/api';
import Navigation from '../Navigation/navigation';

const EditTraining = () => {
  const { staffId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [staffData, setStaffData] = useState(null);
  const [training, setTraining] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [activeTrainingIndex, setActiveTrainingIndex] = useState(null);
  const [newTrainingName, setNewTrainingName] = useState('');
  const [newTrainingStatus, setNewTrainingStatus] = useState('2'); // Default to In-service
  const [addingTraining, setAddingTraining] = useState(false);
  const [referenceData, setReferenceData] = useState({
    trainings: [],
    trainingStatuses: [],
    organizations: []
  });

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    if (staffId) {
      fetchStaffData();
      fetchTraining();
      fetchReferenceData();
    }
  }, [staffId, isAuthenticated, user, navigate]);

  const fetchStaffData = async () => {
    try {
      const response = await api.get(`/api/staff/${staffId}`);
      setStaffData(response.data);
    } catch (error) {
      console.error('Error fetching staff data:', error);
      setError('Error retrieving staff information.');
    }
  };

  const fetchTraining = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/staff/${staffId}/training`);
      setTraining(response.data || []);
    } catch (error) {
      console.error('Error fetching training:', error);
      setError('Error retrieving training information.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [trainings, trainingStatuses, organizations] = await Promise.all([
        api.get('/api/reference/trainings'),
        api.get('/api/reference/training-statuses'),
        api.get('/api/reference/organizations')
      ]);
      
      // Helper function to extract data from response (handles both direct arrays and wrapped responses)
      const extractData = (response) => {
        if (Array.isArray(response.data)) {
          return response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          return response.data.data;
        } else if (response.data && response.data.success && Array.isArray(response.data.data)) {
          return response.data.data;
        }
        return [];
      };
      
      setReferenceData({
        trainings: extractData(trainings),
        trainingStatuses: extractData(trainingStatuses),
        organizations: extractData(organizations)
      });
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // Handle date string directly if already in YYYY-MM-DD format
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      return dateString.split('T')[0];
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    // Use local date to avoid timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get status name from ID
  const getStatusName = (statusId) => {
    if (referenceData.trainingStatuses) {
      const status = referenceData.trainingStatuses.find(s => s.id === parseInt(statusId));
      return status ? status.status_name : 'In-service';
    }
    // Fallback to hardcoded values if data not loaded yet
    const statusMap = {
      '1': 'Pre-service',
      '2': 'In-service', 
      '3': 'Refresher'
    };
    return statusMap[statusId] || 'In-service';
  };

  const addTraining = () => {
    const newTraining = {
      training_id: '',
      training_location: '',
      start_date: '',
      end_date: '',
      give_certificate: false,
      organization_ids: [],
      given_by: null
    };
    setTraining(prev => [...prev, newTraining]);
  };

  const updateTraining = (index, field, value) => {
    setTraining(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeTraining = (index) => {
    setTraining(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddNewTraining = async () => {
    if (!newTrainingName.trim()) {
      alert('Please enter a training name');
      return;
    }

    setAddingTraining(true);
    try {
      const response = await api.post('/api/trainings', {
        training_name: newTrainingName.trim(),
        training_status: parseInt(newTrainingStatus)
      });

      const { success, training: newTrainingData } = response.data;
      
      if (success && newTrainingData) {
        const newTraining = {
          id: newTrainingData.id,
          training_name: newTrainingData.training_name,
          training_status: newTrainingData.training_status,
          status_name: newTrainingData.status_name,
          created_at: newTrainingData.created_at
        };

        setReferenceData(prev => ({
          ...prev,
          trainings: [...prev.trainings, newTraining]
        }));
        
        // Auto-populate the new training in the form that opened the modal
        if (activeTrainingIndex !== null) {
          updateTraining(activeTrainingIndex, 'training_id', newTrainingData.id.toString());
        }
        
        setNewTrainingName('');
        setNewTrainingStatus('2');
        setShowModal(false);
        setActiveTrainingIndex(null);
        
        alert(`Training "${newTrainingData.training_name}" added successfully!`);
      } else {
        const newTraining = {
          id: response.data.id,
          training_name: newTrainingName.trim(),
          training_status: parseInt(newTrainingStatus),
          status_name: getStatusName(newTrainingStatus)
        };

        setReferenceData(prev => ({
          ...prev,
          trainings: [...prev.trainings, newTraining]
        }));
        
        // Auto-populate the new training in the form that opened the modal
        if (activeTrainingIndex !== null) {
          updateTraining(activeTrainingIndex, 'training_id', response.data.id.toString());
        }
        
        setNewTrainingName('');
        setNewTrainingStatus('2');
        setShowModal(false);
        setActiveTrainingIndex(null);
        alert('Training added successfully!');
      }
    } catch (error) {
      console.error('Error adding training:', error);
      alert('Error adding training. Please try again.');
    } finally {
      setAddingTraining(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      await api.put(`/api/staff/${staffId}/training`, { training });
      setSuccessMessage('Training information updated successfully!');
      setTimeout(() => {
        navigate(`/edit-staff/${staffId}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating training:', error);
      setError(error.response?.data?.message || 'Error updating training information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/edit-staff/${staffId}`);
  };

  if (loading && !staffData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-6xl mx-auto p-5 pt-20">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h4 className="text-xl font-semibold text-gray-800">Loading Training Information...</h4>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-6xl mx-auto p-5 pt-20">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">🎓 Edit Training Information</h1>
              <p className="text-gray-600">
                Managing training for: <strong>{staffData?.fullname}</strong> (ID: {staffData?.staff_id})
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Success and Error Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold">Training Information</h2>
                <p className="text-blue-100 mt-1">Manage training records and certifications</p>
              </div>
              <button 
                type="button" 
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors" 
                onClick={addTraining}
              >
                Add Training Record
              </button>
            </div>
          </div>

          {/* Modal for Adding New Training */}
          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Add New Training</h3>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Training Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newTrainingName}
                      onChange={(e) => setNewTrainingName(e.target.value)}
                      placeholder="Enter training name..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Training Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newTrainingStatus}
                      onChange={(e) => setNewTrainingStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {referenceData.trainingStatuses && referenceData.trainingStatuses.length > 0 ? (
                        referenceData.trainingStatuses.map(status => (
                          <option key={status.id} value={status.id}>
                            {status.status_name}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="1">Pre-service</option>
                          <option value="2">In-service</option>
                          <option value="3">Refresher</option>
                        </>
                      )}
                    </select>
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleAddNewTraining}
                      disabled={addingTraining || !newTrainingName.trim()}
                    >
                      {addingTraining ? 'Adding...' : 'Add Training'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="p-6 space-y-6">
            {training.length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-4xl mb-4">🎓</div>
                <p className="text-lg font-medium text-gray-600">No training information added yet</p>
                <p className="text-sm text-gray-500 mt-2">Click "Add Training Record" to get started</p>
              </div>
            )}

            {training.map((trainingItem, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Training #{index + 1}</h3>
                  <button
                    type="button"
                    className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm transition-colors"
                    onClick={() => removeTraining(index)}
                  >
                    Remove
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Training</label>
                    <select
                      value={trainingItem.training_id}
                      onChange={(e) => {
                        if (e.target.value === 'add_new') {
                          setActiveTrainingIndex(index);
                          setShowModal(true);
                        } else {
                          updateTraining(index, 'training_id', e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    >
                      <option value="">Select Training</option>
                      {referenceData.trainings.map((train) => (
                        <option key={train.id} value={train.id}>
                          {train.training_name} ({train.status_name || getStatusName(train.training_status)})
                        </option>
                      ))}
                      <option value="add_new" className="font-semibold text-blue-600">
                        + Add New Training
                      </option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      value={trainingItem.training_location}
                      onChange={(e) => updateTraining(index, 'training_location', e.target.value)}
                      placeholder="Training location..."
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(trainingItem.start_date)}
                      onChange={(e) => updateTraining(index, 'start_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(trainingItem.end_date)}
                      onChange={(e) => updateTraining(index, 'end_date', e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white border border-gray-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={trainingItem.give_certificate}
                        onChange={(e) => updateTraining(index, 'give_certificate', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span>Certificate Given</span>
                    </label>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Certifying Organizations</label>
                    <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {referenceData.organizations.map(org => {
                        // Ensure both values are the same type for comparison
                        const orgIds = (trainingItem.organization_ids || []).map(id => parseInt(id));
                        const isChecked = orgIds.includes(parseInt(org.id));
                        
                        return (
                        <label key={org.id} className="flex items-center space-x-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const currentIds = trainingItem.organization_ids || [];
                              let newIds;
                              if (e.target.checked) {
                                newIds = [...currentIds, parseInt(org.id)];
                              } else {
                                newIds = currentIds.filter(id => parseInt(id) !== parseInt(org.id));
                              }
                              updateTraining(index, 'organization_ids', newIds);
                              // Also update given_by with the first selected organization for compatibility
                              updateTraining(index, 'given_by', newIds.length > 0 ? newIds[0] : null);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                          />
                          <span>{org.organization_name}</span>
                        </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Training Summary */}
                {trainingItem.training_id && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-sm text-gray-800">
                      <strong>Summary:</strong> {referenceData.trainings.find(t => t.id === trainingItem.training_id)?.training_name}
                      {trainingItem.training_location && ` at ${trainingItem.training_location}`}
                      {trainingItem.start_date && ` (${new Date(trainingItem.start_date).toLocaleDateString()}`}
                      {trainingItem.end_date && ` - ${new Date(trainingItem.end_date).toLocaleDateString()}`}
                      {trainingItem.start_date && !trainingItem.end_date && ' - Ongoing'}
                      {trainingItem.start_date && ')'}
                      {trainingItem.give_certificate && ' • Certificate Awarded'}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTraining;
